/**
 * The sign-in endpoints.
 *
 * Written as plain fetch rather than Better Auth's browser client. The surface
 * is three POSTs and a GET, and §22.2 asks the player bundle to stay small
 * because it loads on a phone over venue Wi-Fi — a client library for four
 * requests is a poor trade there.
 *
 * Every call sends credentials: the session is an httpOnly cookie, and fetch
 * omits cookies by default on some paths. Without it a sign-in appears to
 * succeed and then nothing is signed in, which is a confusing bug to chase.
 */

export interface SignedInUser {
  email: string;
  name: string;
}

export interface AuthState {
  signedIn: boolean;
  email?: string;
  name?: string;
  /** Whether this account may open the console for the current tenant. */
  facilitator: boolean;
  role: string | null;
  /** True when the console should offer the enrollment key prompt. */
  canEnroll: boolean;
  /** "vouched" means this deployment cannot send email at all. */
  identityMode: "verified" | "vouched";
}

export class AuthError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Present on a 429, so the UI can count down instead of guessing. */
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

async function post(path: string, body: unknown): Promise<unknown> {
  const response = await fetch(`/api/auth${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  const payload: unknown = text.length > 0 ? JSON.parse(text) : null;

  if (!response.ok) {
    const envelope = payload as
      | { error?: { message?: string; details?: { retryAfterSeconds?: number } } }
      | { message?: string }
      | null;
    const wrapped = (envelope as { error?: { message?: string } })?.error;
    const message =
      wrapped?.message ??
      (envelope as { message?: string })?.message ??
      "That did not work. Please try again.";
    const retry = (envelope as { error?: { details?: { retryAfterSeconds?: number } } })?.error
      ?.details?.retryAfterSeconds;
    throw new AuthError(response.status, message, typeof retry === "number" ? retry : null);
  }

  return payload;
}

/** Ask for a code. The response says nothing about whether the address exists. */
export async function sendCode(email: string): Promise<void> {
  await post("/email-otp/send-verification-otp", { email: email.trim(), type: "sign-in" });
}

/** Exchange a code for a session cookie. */
export async function verifyCode(email: string, otp: string): Promise<SignedInUser> {
  const result = (await post("/sign-in/email-otp", { email: email.trim(), otp })) as {
    user?: { email?: string; name?: string };
  };
  return { email: result.user?.email ?? email, name: result.user?.name ?? "" };
}

export async function signOut(): Promise<void> {
  await post("/sign-out", {});
}

/** Hand the shared key over once, to become a facilitator permanently. */
export async function enrollFacilitator(key: string): Promise<void> {
  await post("/enroll-facilitator", { key: key.trim() });
}

/**
 * Who the caller is, and what they may open.
 *
 * A network failure resolves to signed-out rather than throwing: the caller
 * renders a sign-in screen, which is the right thing to show when the answer
 * is unknown, and better than an error boundary over the whole console.
 */
export async function fetchAuthState(): Promise<AuthState> {
  try {
    const response = await fetch("/api/auth/me", { credentials: "same-origin" });
    if (!response.ok) throw new Error(String(response.status));
    return (await response.json()) as AuthState;
  } catch {
    return {
      signedIn: false,
      facilitator: false,
      role: null,
      canEnroll: false,
      identityMode: "verified",
    };
  }
}
